import {
  CAPABILITY_ANALYTICS,
  CAPABILITY_APM,
  CAPABILITY_BROWSER,
  CAPABILITY_DATABASE,
  CAPABILITY_LOG,
  CAPABILITY_MOBILE,
  CAPABILITY_SM,
  CAPABILITY_VM,
  CAPABILITY_WSM,
  CAPABILITY_K8M,
  CAPABILITY_MQ,
  CAPABILITY_CLOUD
} from '@/utils/constants';
import apm from '@/utils/templates/apm.template';
import browser from '@/utils/templates/browser.template';
import database from '@/utils/templates/database.template';
import log from '@/utils/templates/log.template';
import mobile from '@/utils/templates/mobile.template';
import sm from '@/utils/templates/sm.template';
import vm from '@/utils/templates/vm.template';
import k8m from '@/utils/templates/k8m.template';
import mq from '@/utils/templates/mq.template';
import analytics from '@/utils/templates/analytics.template';
import cloud from '@/utils/templates/cloud';
import i18n from '@/plugins/i18n';
import {
  APM_RESOURCE_GROUP,
  BROWSER_RESOURCE_GROUP,
  DATABASE_RESOURCE_GROUP,
  VM_RESOURCE_GROUP,
  MOBILE_RESOURCE_GROUP,
  K8S_RESOURCE_GROUP,
  CLOUD_RESOURCE_GROUP,
  LOG_RESOURCE_GROUP,
  ANALYTICS_RESOURCE_GROUP,
  MQ_RESOURCE_GROUP,
  SYNTHETIC_RESOURCE_GROUP,
} from '@/router/constant';

export const CapabilityType = new Map<String, String>();
CapabilityType.set(CAPABILITY_SM, 'S');
CapabilityType.set(CAPABILITY_BROWSER, 'B');
CapabilityType.set(CAPABILITY_APM, 'A');
CapabilityType.set(CAPABILITY_DATABASE, 'D');
CapabilityType.set(CAPABILITY_VM, 'V');
CapabilityType.set(CAPABILITY_MOBILE, 'M');
CapabilityType.set(CAPABILITY_WSM, 'W');
CapabilityType.set(CAPABILITY_K8M, 'K');
CapabilityType.set(CAPABILITY_LOG, 'L');
CapabilityType.set(CAPABILITY_MQ,'Q');
CapabilityType.set(CAPABILITY_ANALYTICS, 'ANL');
CapabilityType.set(CAPABILITY_CLOUD, 'C');

export const InitOperationsOptions = [
  {
    key: 'GT',
    label: 'Greater than '
  },
  {
    key: 'LT',
    label: 'Lesser than '
  },
  {
    key: 'EQ',
    label: 'Equal to '
  },
  {
    key: 'GTE',
    label: 'Greater than or equal to '
  },
  {
    key: 'LTE',
    label: 'Lesser than or equal to '
  }
];

export const AlertCapabilities = [
  {
    key: CAPABILITY_ANALYTICS,
    label: analytics.name,
    resourceGroup: ANALYTICS_RESOURCE_GROUP,
  },
  {
    key: CAPABILITY_APM,
    label: apm.name,
    resourceGroup: APM_RESOURCE_GROUP,
  },
  {
    key: CAPABILITY_BROWSER,
    label: browser.name,
    resourceGroup: BROWSER_RESOURCE_GROUP,
  },
  {
    key: CAPABILITY_MOBILE,
    label: mobile.name,
    resourceGroup: MOBILE_RESOURCE_GROUP,
  },
  {
    key: CAPABILITY_VM,
    label: vm.name,
    resourceGroup: VM_RESOURCE_GROUP,
  },
  {
    key: CAPABILITY_SM,
    label: sm.name,
    resourceGroup: SYNTHETIC_RESOURCE_GROUP,
  },
  {
    key: CAPABILITY_DATABASE,
    label: database.name,
    resourceGroup: DATABASE_RESOURCE_GROUP,
  },
  {
    key: CAPABILITY_K8M,
    label: k8m.name,
    resourceGroup: K8S_RESOURCE_GROUP,
  },
  {
    key: CAPABILITY_LOG,
    label: log.name,
    resourceGroup: LOG_RESOURCE_GROUP,
  },
  {
    key: CAPABILITY_MQ,
    label: mq.name,
    resourceGroup: MQ_RESOURCE_GROUP,
  },
  {
    key: CAPABILITY_CLOUD,
    label: cloud.name,
    resourceGroup: CLOUD_RESOURCE_GROUP,
  },
  // Disabled WSM as it is not supported currently.
  // {
  //   key: CAPABILITY_WSM,
  //   label: wsm.name
  // }
];

export const MeasurementUnits = {
  TIME: [
    {
      unit: 'ms',
      multiplier: 1
    },
    {
      unit: 'sec',
      multiplier: 1000
    },
    {
      unit: 'min',
      multiplier: 60000
    },
    {
      unit: 'hrs',
      multiplier: 3600000
    }
  ],
  NUMBER: [
    {
      unit: 'K',
      multiplier: 1
    }
  ],
  STORAGE: [
    {
      unit: 'Bytes',
      multiplier: 1
    },
    {
      unit: 'KB',
      multiplier: 1024
    },
    {
      unit: 'MB',
      multiplier: 1048576
    },
    {
      unit: 'GB',
      multiplier: 1073741824
    }
  ]
};

export enum Operations {
  GT = '>',
  GTE = '&ge;',
  LT = '<',
  LTE = '&leq;',
  EQ = '='
}
export enum AggTypes {
  AVG = 'Average',
  SUM = 'Sum',
  COUNT = 'Count',
  APDEX = 'Apdex',
  DEFAULT = 'Default',
  DYNAMIC = 'Dynamic Baseline',
  ANOMALY = 'Anomaly count'
}

export enum AlertActions {
  DEL = 'DEL',
  STATUS = 'STATUS',
  EDIT = 'EDIT'
}
export const ErrorCodes = {
  key: 'errorCode',
  label: 'Error code',
  customMsg: 'Error count for code ',
  errorCodes: [
    { '400': '400' },
    { '401': '401' },
    { '402': '402' },
    { '403': '403' },
    { '404': '404' },
    { '405': '405' },
    { '406': '406' },
    { '407': '407' },
    { '408': '408' },
    { '409': '409' },
    { '429': '429' },
    { '500': '500' },
    { '501': '501' },
    { '502': '502' },
    { '503': '503' },
    { '504': '504' },
    { '511': '511' }
  ]
};

export const StatusType = {
  k8s_namespace_v2_namespace_status: [
    { 'Active': i18n.global.t('k8.active') },
    { 'Terminating': i18n.global.t('k8.terminating') },
    { 'Unknown': i18n.global.t('k8.unknown') },
  ],
  k8s_node_v2_status: [
    { 'true': i18n.global.t('k8.ready') },
    { 'false': i18n.global.t('k8.notReady') },
    { 'unknown': i18n.global.t('k8.unknown') },
  ],
  k8s_deployment_v2_status: [
    { 'true': i18n.global.t('k8.available')},
    { 'false': i18n.global.t('k8.unAvailable') },
    { 'unknown': i18n.global.t('k8.unknown') },
  ],
  k8s_pods_v2_status_ready: [
    { 'true': i18n.global.t('k8.ready') },
    { 'false': i18n.global.t('k8.notReady') },
    { 'unknown': i18n.global.t('k8.unknown') },
  ],
  k8s_pods_v2_status_scheduled: [
    { 'true': i18n.global.t('k8.scheduled') },
    { 'false': i18n.global.t('k8.unScheduled') },
    { 'unknown': i18n.global.t('k8.unknown') },
  ],
  k8s_pods_v2_status: [
    { 'Running': i18n.global.t('k8.running') },
    { 'Failed': i18n.global.t('k8.failed') },
    { 'Pending': i18n.global.t('k8.pending') },
    { 'Succeeded': i18n.global.t('k8.succeeded') },
    { 'Unknown': i18n.global.t('k8.unknown') },
  ],
};

